import { useEffect, useRef } from 'react'

import { useRouter } from 'next/router'

import { trackUserExitIntent } from '@/utils/analytics'

import { isExitIntent } from './utils'

const WAIT_TIME = 20000 // 20 sec

type ExitEventType = 'idle' | 'exit_intent'

const useExitIntentTracking = (
    productionId: number = 0,
    performerId: number = 0,
    waitTime: number = WAIT_TIME,
): void => {
    const { pathname } = useRouter()

    const pageType: string = pathname.includes('/production')
        ? 'production'
        : pathname.includes('/performer')
        ? 'performer'
        : ''

    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const fireEvent = (eventType: ExitEventType) => {
        if ((productionId || performerId) && pageType) {
            trackUserExitIntent({
                name: 'Exit_Intent',
                eventType,
                pageType,
                performerId,
                productionId,
            })
            removeEventListeners()
        }
    }

    const resetTimer = (eventType: ExitEventType) => {
        if (timeoutRef.current !== null) {
            clearTimeout(timeoutRef.current)
        }
        timeoutRef.current = setTimeout(() => {
            fireEvent(eventType)
        }, waitTime)
    }

    const fireIdleEvent = () => {
        resetTimer('idle')
    }

    const fireExitIntentEvent = () => {
        if (timeoutRef.current !== null) {
            clearTimeout(timeoutRef.current)
        }
        fireEvent('exit_intent')
    }

    const handleMouseOut = (e: MouseEvent) => {
        if (isExitIntent(e)) fireExitIntentEvent()
    }

    const removeEventListeners = () => {
        if (timeoutRef.current !== null) {
            clearTimeout(timeoutRef.current)
        }
        document.removeEventListener('mousemove', fireIdleEvent, true)
        document.removeEventListener('keydown', fireIdleEvent, true)
        document.removeEventListener('scroll', fireIdleEvent, true)

        document.removeEventListener('touchstart', fireIdleEvent, true)
        document.removeEventListener('touchend', fireIdleEvent, true)
        document.removeEventListener('touchcancel', fireIdleEvent, true)
        document.removeEventListener('touchmove', fireIdleEvent, true)

        document.removeEventListener('mouseout', handleMouseOut, true)
    }

    useEffect(() => {
        removeEventListeners()
        document.addEventListener('scroll', fireIdleEvent, true)
        document.addEventListener('mousemove', fireIdleEvent, true)
        document.addEventListener('keydown', fireIdleEvent, true)

        document.addEventListener('touchstart', fireIdleEvent, true)
        document.addEventListener('touchend', fireIdleEvent, true)
        document.addEventListener('touchcancel', fireIdleEvent, true)
        document.addEventListener('touchmove', fireIdleEvent, true)

        document.addEventListener('mouseout', handleMouseOut, true)
        return () => {
            removeEventListeners()
        }
    }, [productionId, performerId])
}

export default useExitIntentTracking
